/**
 * Administration of Medication Component
 * 
 * Displays a list of medication administration forms for parents
 * Features:
 * - Shows all submitted medication administration forms
 * - Allows adding new medication administration requests
 * - View medication logs for each form
 * - Edit existing forms (if allowed)
 * - Type = 1 (Medication)
 */

import React, { useState, useEffect } from 'react'
import { Row, Col, Container, Button, Badge } from 'react-bootstrap'
import Card from '../../../../components/Card'
import { useNavigate, Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { 
  fetchMedicationList,
  deleteMedication, 
  selectMedicationList, 
  selectListLoading, 
  selectListError, 
  selectMedicationMeta,
  clearErrors 
} from '../../../../store/medication/medicationSlice';

import LoaderModal from '../../../../components/loaderModal';
import NoDataFound from '../../../../components/noDataFound';

//Sweet alert
import Swal from 'sweetalert2'

const MedicationAdministrationList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Redux state
  const medicationList = useSelector(selectMedicationList);
  const loading = useSelector(selectListLoading);
  const error = useSelector(selectListError);
  const meta = useSelector(selectMedicationMeta);

  const [page, setPage] = useState(1);
  const [isFirst, setIsFirst] = useState(true);
  
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    setIsFirst(false)
  }, [isFirst]);

  // Load medication list on component mount
  useEffect(() => {
    if (localStorage.getItem("uzoneAuthToken")) {
      dispatch(fetchMedicationList({ type: 1, page: 1, limit: 10 }));
    } else {
      navigate('/')
    }
  }, [dispatch, navigate]);

  // Handle API errors
  useEffect(() => {
    if (error) {
      Swal.fire({
        title: 'Error',
        text: error,
        icon: 'error',
        confirmButtonText: 'Okay'
      }).then(() => {
        dispatch(clearErrors());
      });
    }
  }, [error, dispatch]);

  // Load more data
  const loadMore = () => {
    if (page < meta.last_page) {
      const nextPage = page + 1;
      dispatch(fetchMedicationList({ type: 1, page: nextPage, limit: 10 }));
      setPage(nextPage);
    }
  };

  // Format date helper function
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // Format time helper function
  const formatTime = (timeString) => {
    if (!timeString) return null;
    const [hours, minutes] = timeString.split(':');
    const hour24 = parseInt(hours);
    const min = parseInt(minutes);
    
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    const formattedMinutes = min.toString().padStart(2, '0');
    
    return `${hour12}:${formattedMinutes} ${ampm}`;
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 1:
        return <Badge bg="success">Active</Badge>;
      case 0:
        return <Badge bg="secondary">Inactive</Badge>;
      default:
        return <Badge bg="warning">Pending</Badge>;
    }
  };

  // Handle delete medication
  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteMedication({ id, type: 1 })).then((response) => {
          if (response.meta.requestStatus === 'fulfilled') {
            Swal.fire({
              title: 'Deleted!',
              text: 'Medication has been deleted successfully.',
              icon: 'success',
              timer: 2000,
              showConfirmButton: false
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.payload || 'Failed to delete medication.',
              icon: 'error',
              confirmButtonText: 'Okay'
            });
          }
        });
      }
    });
  };

  return (
    <>
      <LoaderModal showLoader={loading && page === 1} />
      <div id='content-page' className='content-page'>
        <Container>
          <Row>
            <Col sm="12">
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <div className="header-title">
                    <h4 className="card-title">
                      <i className="ri-medicine-bottle-line me-2"></i>
                      Administration of Medication
                    </h4>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted">Total: {meta.total} forms</span>
                    <Link 
                      to="/dashboard/app/medication/add" 
                      className="btn btn-primary btn-sm"
                    >
                      <i className="ri-add-line me-1"></i>
                      Add New Request
                    </Link>
                  </div>
                </Card.Header>
                <Card.Body>
                  {medicationList?.length > 0 ? (
                    <>
                      <div className="table-responsive">
                        <table className="table table-striped table-hover">
                          <thead className="bg-primary text-white">
                            <tr>
                              <th scope="col">#</th>
                              <th scope="col">Medication Details</th>
                              <th scope="col">Schedule</th>
                              <th scope="col">Duration</th>
                              <th scope="col">Status</th>
                              <th scope="col">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {medicationList.map((medication, index) => (
                              <tr key={medication.id} className="align-middle">
                                <th scope="row">{(page - 1) * 10 + index + 1}</th>
                                <td>
                                  <div>
                                    <h6 className="mb-1">{medication.medication_name}</h6>
                                    <small className="text-muted">
                                      Parent: {medication.parent_name}
                                    </small>
                                  </div>
                                </td>
                                <td>
                                  <span className="fw-semibold">
                                    {formatTime(medication.times_to_be_administered)}
                                  </span>
                                </td>
                                <td>
                                  <div>
                                    <small className="d-block">
                                      <strong>Start:</strong> {formatDate(medication.date_start)}
                                    </small>
                                    <small className="d-block">
                                      <strong>End:</strong> {formatDate(medication.date_end)}
                                    </small>
                                  </div>
                                </td>
                                <td>
                                  {getStatusBadge(medication.status)}
                                </td>
                                <td>
                                  <div className="d-flex gap-1">
                                    <Link
                                      to={`/dashboard/app/medication/logs/${medication.id}`}
                                      className="btn btn-outline-info btn-sm"
                                      title="View Logs"
                                    >
                                      <i className="ri-file-list-2-line"></i>
                                    </Link>
                                    {(medication.status === 1 || medication.status === '1') && (
                                      <Link
                                        to={`/dashboard/app/medication/edit/${medication.id}`}
                                        className="btn btn-outline-warning btn-sm"
                                        title="Edit"
                                      >
                                        <i className="ri-edit-line"></i>
                                      </Link>
                                    )}
                                    {(medication.status === 1 || medication.status === '1') && 
                                     new Date(medication.date_start) > new Date() && (
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleDelete(medication.id)}
                                        title="Delete"
                                      >
                                        <i className="ri-delete-bin-line"></i>
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Load More Button */}
                      {page < meta.last_page && (
                        <div className="text-center mt-3">
                          <Button 
                            variant="outline-primary" 
                            onClick={loadMore}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </span>
                                Loading...
                              </>
                            ) : (
                              <>
                                <i className="ri-arrow-down-line me-1"></i>
                                Load More
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <NoDataFound />
                  )}
                  
                  {/* Summary Cards */}
                  {medicationList?.length > 0 && (
                    <Row className="mt-4">
                      <Col md={4}>
                        <Card className="bg-light border-0">
                          <Card.Body className="text-center">
                            <i className="ri-medicine-bottle-fill display-4 text-success mb-2"></i>
                            <h5 className="mb-1">
                              {medicationList.filter(m => m.status === 1).length}
                            </h5>
                            <small className="text-muted">Active Medications</small>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={4}>
                        <Card className="bg-light border-0">
                          <Card.Body className="text-center">
                            <i className="ri-time-line display-4 text-primary mb-2"></i>
                            <h5 className="mb-1">
                              {medicationList.filter(m => 
                                new Date(m.date_end) > new Date()
                              ).length}
                            </h5>
                            <small className="text-muted">Ongoing Treatments</small>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={4}>
                        <Card className="bg-light border-0">
                          <Card.Body className="text-center">
                            <i className="ri-check-line display-4 text-warning mb-2"></i>
                            <h5 className="mb-1">
                              {medicationList.filter(m => 
                                new Date(m.date_end) < new Date()
                              ).length}
                            </h5>
                            <small className="text-muted">Completed</small>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  )
}

export default MedicationAdministrationList